import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { finalize } from 'rxjs';
import { BulletizeNumberedHeadingsPipe } from '../shared/pipes/bulletize-numbered-headings.pipe';
import { SoapNormalizePipe } from '../shared/pipes/soap-normalize.pipe';
import {
  AdminAnalysisPreviewResponse,
  AdminReplayRangeResponse,
  AdminReplayRunSummary,
  AdminReplayState,
  DevAdminService,
  SoapComparisonResponse
} from './devadmin.service';

@Component({
  selector: 'app-devadmin',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    ProgressSpinnerModule,
    ToastModule,
    BulletizeNumberedHeadingsPipe,
    SoapNormalizePipe
  ],
  templateUrl: './devadmin.component.html',
  styleUrls: ['./devadmin.component.scss'],
  providers: [MessageService]
})
export class DevAdminComponent implements OnInit {
  transcriptText = '';
  specialty = 'General Neurology';
  analysisLoading = false;
  analysisResult: AdminAnalysisPreviewResponse | null = null;

  transcriptionId = '';
  desiredSoapText = '';
  compareLoading = false;
  compareResult: SoapComparisonResponse | null = null;

  replayStartId = '';
  replayEndId = '';
  replayVersion: 'v2' | 'v3' = 'v2';
  replayLoading = false;
  replayResult: AdminReplayRangeResponse | null = null;
  replayState: AdminReplayState | null = null;
  replayRuns: AdminReplayRunSummary[] = [];

  constructor(
    private devAdminService: DevAdminService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.refreshReplayMeta();
  }

  refreshReplayMeta(): void {
    this.devAdminService.fetchReplayState().subscribe({
      next: (state) => {
        this.replayState = state;
        if (!this.replayStartId && state?.suggestedNextStartId) {
          this.replayStartId = state.suggestedNextStartId.toString();
        }
        this.cdr.markForCheck();
      },
      error: () => {
        // ignore - optional in UI
      }
    });

    this.devAdminService.fetchReplayRuns().subscribe({
      next: (runs) => {
        this.replayRuns = runs || [];
        this.cdr.markForCheck();
      },
      error: () => {
        // ignore - optional in UI
      }
    });
  }

  runReplayRange(): void {
    const start = Number(this.replayStartId.trim());
    const end = Number(this.replayEndId.trim());
    if (!Number.isFinite(start) || !Number.isFinite(end) || start <= 0 || end <= 0 || end < start) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid range',
        detail: 'Provide a valid start/end transcription ID (end >= start).'
      });
      return;
    }

    this.replayLoading = true;
    this.replayResult = null;

    this.devAdminService.replaySoapRange({ startId: start, endId: end, analysisVersion: this.replayVersion }).pipe(
      finalize(() => {
        this.replayLoading = false;
        this.refreshReplayMeta();
        this.cdr.markForCheck();
      })
    ).subscribe({
      next: (response) => {
        this.replayResult = response;
        this.cdr.markForCheck();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Replay failed',
          detail: 'Unable to replay range. Check backend logs.'
        });
        this.cdr.markForCheck();
      }
    });
  }

  runAnalysis(): void {
    const text = this.transcriptText.trim();
    if (!text) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Missing transcript',
        detail: 'Paste a transcript before running analysis.'
      });
      return;
    }

    this.analysisLoading = true;
    this.analysisResult = null;

    this.devAdminService.runAnalysisPreview({
      transcriptText: text,
      specialty: this.specialty?.trim() || undefined
    }).pipe(
      finalize(() => {
        this.analysisLoading = false;
        this.cdr.markForCheck();
      })
    ).subscribe({
      next: (response) => {
        this.analysisResult = response;
        if (response?.transcriptionId) {
          this.transcriptionId = response.transcriptionId.toString();
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Analysis failed',
          detail: 'Unable to generate analysis. Check backend logs.'
        });
        this.cdr.markForCheck();
      }
    });
  }

  fetchAnalysisById(): void {
    const id = this.transcriptionId.trim();
    if (!id) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Missing transcription ID',
        detail: 'Provide a transcription ID to fetch a saved analysis.'
      });
      return;
    }

    this.analysisLoading = true;
    this.analysisResult = null;

    this.devAdminService.fetchAnalysisById(id).pipe(
      finalize(() => {
        this.analysisLoading = false;
        this.cdr.markForCheck();
      })
    ).subscribe({
      next: (response) => {
        this.analysisResult = response;
        this.cdr.markForCheck();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Fetch failed',
          detail: 'Unable to fetch saved analysis.'
        });
        this.cdr.markForCheck();
      }
    });
  }

  compareSoap(): void {
    const id = this.transcriptionId.trim();
    const desired = this.desiredSoapText.trim();
    if (!id || !desired) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Missing inputs',
        detail: 'Provide transcription ID and desired SOAP output.'
      });
      return;
    }

    this.compareLoading = true;
    this.compareResult = null;

    this.devAdminService.compareSoap({
      transcriptionId: id,
      desiredSoapText: desired
    }).pipe(
      finalize(() => {
        this.compareLoading = false;
        this.cdr.markForCheck();
      })
    ).subscribe({
      next: (response) => {
        this.compareResult = response;
        this.cdr.markForCheck();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Compare failed',
          detail: 'Unable to compare SOAP output.'
        });
        this.cdr.markForCheck();
      }
    });
  }


  getSoapSection(soap: { subjective?: string; objective?: string; assessment?: string; plan?: string } | undefined, section: string): string {
    if (!soap) {
      return '';
    }
    switch (section) {
      case 'subjective':
        return soap.subjective || '';
      case 'objective':
        return soap.objective || '';
      case 'assessment':
        return soap.assessment || '';
      case 'plan':
        return soap.plan || '';
      default:
        return '';
    }
  }

  useTranscriptionId(): void {
    this.fetchAnalysisById();
  }

  reanalyzeByTranscriptionId(): void {
    const id = this.transcriptionId.trim();
    if (!id) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Missing transcription ID',
        detail: 'Provide a transcription ID to reanalyze.'
      });
      return;
    }

    this.analysisLoading = true;
    this.analysisResult = null;

    this.devAdminService.reanalyze(id).pipe(
      finalize(() => {
        this.analysisLoading = false;
        this.cdr.markForCheck();
      })
    ).subscribe({
      next: (response) => {
        this.analysisResult = response;
        if (response?.transcriptionId) {
          this.transcriptionId = response.transcriptionId.toString();
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Reanalyze failed',
          detail: 'Unable to re-run analysis.'
        });
        this.cdr.markForCheck();
      }
    });
  }

  resetAll(): void {
    this.transcriptText = '';
    this.specialty = 'General Neurology';
    this.analysisLoading = false;
    this.analysisResult = null;
    this.transcriptionId = '';
    this.desiredSoapText = '';
    this.compareLoading = false;
    this.compareResult = null;
    this.replayStartId = '';
    this.replayEndId = '';
    this.replayVersion = 'v2';
    this.replayLoading = false;
    this.replayResult = null;
    this.cdr.markForCheck();
  }
}
